import { Component, EventEmitter, HostListener, Input, OnInit, Output } from "@angular/core";
import { DeviceTypesService } from "../../services/device-types.service";
import { DeviceTypeRequestCategory } from "../../models/device/device-type-request-category";
import { LookupService } from "../../services/lookup.service";
import { forkJoin, Observable, of } from "rxjs";
import { KeyValueObject } from "../../models/core/KeyValueObject";
import { DeviceModelsService } from "../../services/device-models.service";
import { DeviceModelDto } from "../../models/device/device-model-dto";
import { Guid } from "guid-typescript";
import { For1CService } from "../../services/for-1c.service";
import { catchError, defaultIfEmpty, filter, map, tap } from "rxjs/operators";
import { NewRequestDevice } from "../../models/request/new-request/new-request-device";
import { DeviceModelNomenclatureDto } from "../../models/for-1c/device-model-nomenclature.dto";
import { NomenclatureService } from "../../services/nomenclature.service";
import { ActionDataResult } from "../../models/core/ActionDataResult";
import { WarehousesService } from "../../services/warehouses.service";
import { BwtTwoOwnCentralWarehousesDto } from "../../models/warehouse/bwt-two-own-central-warehouses.dto";
import { RequestServicesService } from "../../services/request-services.service";
import { RequestDevicesService } from "../../services/request-devices.service";

interface DeviceTreeItem {
    id: number;
    uid: string;
    name: string;
    sku: string;
    items: DeviceTreeItem[];
    deviceModels: DeviceTreeItem[];
    isDeviceModel: boolean
    isDeviceSupplier: boolean
    isNomenclature: boolean;
    emptyNomeclature: boolean;
    checked: boolean;
    cost: number;
    originalCost: number;
    count: number;
    count2: number;
    countAlias: string;
    costAlias: string;
    warehouseIntegraCode: string;
    warehouseIntegraCode2: string;
    warehouseName: string;
    warehouseName2: string;
    disabled: boolean;
    neededCount: number | null;
    neededCount2: number;
    deviceModelId: number;
    deviceModelName: string;
    articleNumber: string;
}

class KeyAndCount {
    constructor(key: string, count1: number, count2: number) {
        this.key = key;
        this.count1 = count1;
        this.count2 = count2;
    }

    key: string;
    count1: number;
    count2: number;
}

@Component({
    selector: 'devices-hierarchical-selector',
    templateUrl: './devices-hierarchical-selector.component.html',
    styleUrls: ['./devices-hierarchical-selector.component.scss',
        '../../../../vendor/libs/angular2-ladda/angular2-ladda.scss',
        '../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
    ]
})
export class DevicesHierarchicalSelectorComponent implements OnInit {

    @Input() defaultRequestCategoryId: number;
    @Input() height: number = 600;
    @Input() disableFreeItems: boolean = false;
    @Input() isReclamation: boolean = false;
    @Input() requestId: number = null;
    @Input() serviceCenterId: number = null;
    @Input() showAddAsAdditional: boolean = false;
    @Input() hideWarehouses: boolean = false;

    addAsAdditional: boolean = false;

    @Output() onContinue: EventEmitter<NewRequestDevice[]> = new EventEmitter<NewRequestDevice[]>();
    @Output() onCancel: EventEmitter<any> = new EventEmitter<any>();

    deviceModels: DeviceModelDto[] = [];
    deviceSuppliers: DeviceModelDto[] = [];
    deviceTypeRequestCategories: DeviceTypeRequestCategory[] = [];

    treeDataAll: DeviceTreeItem[] = [];
    treeData: DeviceTreeItem[] = [];
    defaultExpandedKeys: string[] = [];
    expandedKeys: string[] = [];
    filterValue: string;
    bwtTwoOwnCentralWarehouses: BwtTwoOwnCentralWarehousesDto;
    totalServicesAmount: number = 0;
    totalDevicesAmount: number = 0;

    selectedDevices: KeyAndCount[] = [];

    loading: boolean = false;
    isAllDataLoaded: boolean = false;

    private initialDataLoaded = false;
    private nomenclatureLoaded = false;
    private totalsLoaded = false;
    private isApplying = false;

    constructor(
        private deviceTypesService: DeviceTypesService,
        private deviceModelsService: DeviceModelsService,
        private nomenclatureService: NomenclatureService,
        private warehousesService: WarehousesService,
        private requestServicesService: RequestServicesService,
        private requestDevicesService: RequestDevicesService
    ) {
        this.fetchChildren = this.fetchChildren.bind(this)
    }

    ngOnInit(): void {
        this.loading = true;
        this.isAllDataLoaded = false;
        const deviceModels$ = this.deviceModelsService.getAll();
        const deviceTypeRequestCategories$ = this.deviceTypesService.getDeviceTypeRequestCategories();
        const requests: Observable<any>[] = [deviceModels$, deviceTypeRequestCategories$];

        if (!this.hideWarehouses) {
            requests.push(this.warehousesService.getBwtTwoOwnCentralWarehouses(this.requestId, this.serviceCenterId)
                .pipe(
                    defaultIfEmpty(null),
                    catchError(() => of(null))
                ));
        }

        forkJoin(requests).subscribe(
            (results) => {
                const deviceModels = results[0];
                const deviceTypeRequestCategories = results[1];
                let bwtTwoOwnCentralWarehouses = this.hideWarehouses
                    ? new BwtTwoOwnCentralWarehousesDto()
                    : results[2];

                if (!bwtTwoOwnCentralWarehouses) {
                    this.loading = false;
                    this.onCancel.emit();
                    return;
                }

                this.bwtTwoOwnCentralWarehouses = bwtTwoOwnCentralWarehouses;
                this.deviceSuppliers = deviceModels.filter(x => x.parentId == null);
                this.deviceModels = deviceModels.filter(x => x.parentId != null);
                this.deviceTypeRequestCategories = deviceTypeRequestCategories;
                this.initialDataLoaded = true;

                if (!this.hideWarehouses && !!this.requestId) {
                    forkJoin(([
                        this.requestServicesService.getTotalAmount(this.requestId),
                        this.requestDevicesService.getTotalAmount(this.requestId)
                    ])).subscribe(
                        ([totalServicesAmount, totalDevicesAmount]) => {
                            this.totalDevicesAmount = totalDevicesAmount;
                            this.totalServicesAmount = totalServicesAmount;
                            this.totalsLoaded = true;
                            this.updateAllDataLoadedState();
                        },
                        () => {
                            this.totalsLoaded = true;
                            this.updateAllDataLoadedState();
                        }
                    );
                } else {
                    this.totalsLoaded = true;
                }

                this.groupDeviceTypeRequestCategories();
                this.setDefaultExpandedKeys();
                this.updateAllDataLoadedState();
            }, () => { this.loading = false; this.onCancel.emit(); }
        );
    }

    private updateAllDataLoadedState(): void {
        const totalsRequired = !!this.requestId;
        const allLoaded = this.initialDataLoaded
            && this.nomenclatureLoaded
            && (!totalsRequired || this.totalsLoaded);

        if (allLoaded) {
            this.isAllDataLoaded = true;
            this.loading = false;
        }
    }

    groupDeviceTypeRequestCategories() {
        const uniqueRequestCategoryIds = this.distinct(this.deviceTypeRequestCategories.map(x => x.requestCategoryId));

        let allDeviceModelIds: number[] = [];

        this.treeData = uniqueRequestCategoryIds.map(requestCategoryId => {
            let deviceTypes = this.deviceTypeRequestCategories.filter(x => x.requestCategoryId == requestCategoryId);
            let uniqueDeviceTypeIds = this.distinct(deviceTypes.map(x => x.deviceTypeId));

            let deviceTypeItems = uniqueDeviceTypeIds.map(deviceTypeId => {
                let deviceSuppliers = this.deviceSuppliers.filter(x => x.typeId == deviceTypeId && x.requestCategoryId == requestCategoryId);

                let deviceSupplierItems = deviceSuppliers.map(deviceSupplier => {
                    let deviceModels = this.deviceModels.filter(x => x.parentId == deviceSupplier.id && x.requestCategoryId == requestCategoryId);

                    let deviceModelItems = deviceModels.map(deviceModel => {
                        let deviceModelItem = {
                            id: deviceModel.id,
                            name: deviceModel.name,
                            isDeviceModel: true,
                            uid: Guid.create().toString()
                        } as DeviceTreeItem;
                        return deviceModelItem;
                    });

                    let deviceSupplierItem = {
                        id: deviceSupplier.id,
                        isDeviceSupplier: true,
                        name: deviceSupplier.name,
                        items: [],
                        deviceModels: deviceModelItems,
                        uid: Guid.create().toString()
                    } as DeviceTreeItem;

                    deviceModelItems.forEach(x => {
                        if (!allDeviceModelIds.some(d => d == x.id)) {
                            allDeviceModelIds.push(x.id);
                        }
                    });

                    return deviceSupplierItem;
                });

                let deviceTypeName = this.deviceTypeRequestCategories.find(x => x.deviceTypeId == deviceTypeId).deviceTypeName;
                let deviceTypeItem = {
                    id: deviceTypeId,
                    name: deviceTypeName,
                    items: deviceSupplierItems,
                    uid: Guid.create().toString()
                } as DeviceTreeItem;
                return deviceTypeItem;
            });

            let requestCategoryName = this.deviceTypeRequestCategories.find(x => x.requestCategoryId == requestCategoryId).requestCategoryName;
            let requestCategoryItem = {
                id: requestCategoryId,
                name: requestCategoryName,
                items: deviceTypeItems,
                uid: Guid.create().toString()
            } as DeviceTreeItem;
            return requestCategoryItem;
        });

        this.treeDataAll = this.treeData;

        if (this.hideWarehouses) {
            this.treeData.forEach(requestCategoryItem => {
                requestCategoryItem.items.forEach(deviceTypeItem => {
                    deviceTypeItem.items.forEach(deviceSupplierItem => {
                        deviceSupplierItem.items = (deviceSupplierItem.deviceModels || []).map(deviceModel => ({
                            id: deviceModel.id,
                            name: deviceModel.name,
                            isDeviceModel: true,
                            isNomenclature: true,
                            uid: Guid.create().toString(),
                            deviceModelId: deviceModel.id,
                            deviceModelName: deviceModel.name,
                            neededCount: null,
                            neededCount2: 0,
                            checked: false,
                            disabled: false,
                            cost: 0,
                            items: []
                        } as DeviceTreeItem));
                        this.setChildren(deviceSupplierItem.uid, deviceSupplierItem.items);
                    });
                });
            });
            this.treeDataAll = this.treeData;
            this.nomenclatureLoaded = true;
            this.updateAllDataLoadedState();
            return;
        }

        this.nomenclatureService.getAllNomenclatureByDeviceModels(allDeviceModelIds, this.requestId, this.isReclamation, this.serviceCenterId).subscribe((resp) => {
            this.treeData.forEach(requestCategoryItem => {
                requestCategoryItem.items.forEach(deviceTypeItem => {
                    deviceTypeItem.items.forEach(deviceSupplierItem => {
                        let deviceModelIds = deviceSupplierItem.deviceModels.map(x => x.id);
                        let nomenclatures = resp.data.filter(x => deviceModelIds.some(d => d == x.deviceModelId));

                        if (nomenclatures != null && nomenclatures.length > 0) {        
                            deviceSupplierItem.emptyNomeclature = false;
                            deviceSupplierItem.items = nomenclatures.map(nomenclature => {
                                let itemDisabled = this.disableFreeItems && (nomenclature.cost == null || nomenclature.cost == 0);
                                itemDisabled = nomenclature.count === 0 ? false : itemDisabled;
                                return {
                                    name: nomenclature.nomenclature,
                                    sku: nomenclature.sku,
                                    isNomenclature: true,
                                    cost: nomenclature.cost,
                                    originalCost: nomenclature.cost,
                                    uid: Guid.create().toString(),
                                    count: nomenclature.count,
                                    count2: nomenclature.count2,
                                    countAlias: nomenclature.countAlias,
                                    warehouseIntegraCode: nomenclature.warehouseIntegraCode,
                                    warehouseIntegraCode2: nomenclature.warehouseIntegraCode2,
                                    warehouseName: nomenclature.warehouseName,
                                    warehouseName2: nomenclature.warehouseName2,
                                    costAlias: nomenclature.costAlias,
                                    disabled: itemDisabled,
                                    neededCount: 0,
                                    neededCount2: 0,
                                    deviceModelId: nomenclature.deviceModelId,
                                    deviceModelName: nomenclature.deviceModelName,
                                    articleNumber: nomenclature.articleNumber
                                } as DeviceTreeItem;
                            });
                        }
                        else {
                            deviceSupplierItem.items = this.buildEmptyNomenclature();
                        }

                        this.setChildren(deviceSupplierItem.uid, deviceSupplierItem.items);
                    });
                });
            });

            this.treeDataAll = this.treeData;
            this.nomenclatureLoaded = true;
            this.updateAllDataLoadedState();
        }, () => {
            this.nomenclatureLoaded = true;
            this.updateAllDataLoadedState();
        });
    }

    setDefaultExpandedKeys() {
        if (this.defaultRequestCategoryId) {
            let requestCategoryItem = this.treeDataAll.find(x => x.id == this.defaultRequestCategoryId);
            if (requestCategoryItem != null) {
                this.defaultExpandedKeys.push(requestCategoryItem.uid);
            }
        }

        this.expandedKeys = this.defaultExpandedKeys.map(x => x);
    }

    filterValueChange(value: string, previousLoading: boolean = this.loading) {
        this.treeData = JSON.parse(JSON.stringify(this.treeDataAll));

        if (value && value.trim() != '') {
            this.treeData = this.filterTree(this.treeData, value);
        }
        else {
            this.expandedKeys = this.defaultExpandedKeys.map(x => x);
        }   
    }

    filterTree(items: DeviceTreeItem[] = [], value: string): DeviceTreeItem[] {
        let resultItems: DeviceTreeItem[] = [];

        items.forEach(item => {
            let children: DeviceTreeItem[] = [];
            let nameIsMatch = this.nameIsMatch(item, value);
            let skuIsMatch = this.skuIsMatch(item, value);

            if (nameIsMatch || (!nameIsMatch && !item.isDeviceModel) || (!nameIsMatch && !item.isDeviceSupplier)
                || skuIsMatch || (!skuIsMatch && !item.isDeviceModel) || (!skuIsMatch && !item.isDeviceSupplier)) {
                children = this.filterTree(item.items, value);
                nameIsMatch = nameIsMatch || children.length > 0;
                skuIsMatch = skuIsMatch || children.length > 0;
            }

            if (nameIsMatch || skuIsMatch) {
                item.items = children;
                resultItems.push(item);

                if (children.length > 0 && !this.expandedKeys.includes(item.uid)) {
                    this.expandedKeys.push(item.uid);
                }
            }
        });

        return resultItems;
    }

    checkItem(dataItem: DeviceTreeItem, value: boolean) {
        let nomeclatureItem = this.findItem(this.treeDataAll, dataItem.uid);
        const nextCount = value ? 1 : (this.hideWarehouses ? null : 0);
        dataItem.neededCount = nextCount;
        if (nomeclatureItem) {
            nomeclatureItem.checked = value;
            nomeclatureItem.neededCount = nextCount;
            let count2 = null;
            if (!value) {
                count2 = 0;
                nomeclatureItem.neededCount2 = 0;
                nomeclatureItem.cost = nomeclatureItem.originalCost;
                dataItem.cost = nomeclatureItem.originalCost;
            }

            this.addOrUpdateSelectedDevicesCount(dataItem.uid, value ? 1 : 0, count2);
        }
    }

    changeCustomerNeededCount(dataItem: DeviceTreeItem, value: number) {
        if (!dataItem.checked) {
            dataItem.neededCount = null;
            return;
        }

        const parsed = value == null || (value as any) === '' ? NaN : Number(value);
        const count = !isFinite(parsed) || parsed < 1 ? 1 : Math.floor(parsed);
        const nomeclatureItem = this.findItem(this.treeDataAll, dataItem.uid);

        if (dataItem.neededCount === count) {
            dataItem.neededCount = null;
            if (nomeclatureItem) {
                nomeclatureItem.neededCount = null;
            }
            setTimeout(() => {
                dataItem.neededCount = count;
                if (nomeclatureItem) {
                    nomeclatureItem.neededCount = count;
                }
            });
            return;
        }

        dataItem.neededCount = count;
        if (nomeclatureItem) {
            nomeclatureItem.checked = true;
            nomeclatureItem.neededCount = count;
        }
        this.addOrUpdateSelectedDevicesCount(dataItem.uid, count, 0);
    }

    onCustomerCountKeyDown(event: KeyboardEvent) {
        if (event.key === '-' || event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '.' || event.key === ',') {
            event.preventDefault();
        }
    }

    changeNeededCount(dataItem: DeviceTreeItem, value: number) {
        let nomeclatureItem = this.findItem(this.treeDataAll, dataItem.uid);
        if (nomeclatureItem) {
            nomeclatureItem.checked = value !== 0;
            nomeclatureItem.neededCount = value;
            nomeclatureItem.neededCount2 = 0;

            if (!nomeclatureItem.checked) {
                nomeclatureItem.cost = nomeclatureItem.originalCost;
                dataItem.cost = nomeclatureItem.originalCost;
            }

            this.addOrUpdateSelectedDevicesCount(dataItem.uid, value, 0);
        }
    }

    changeNeededCount2(dataItem: DeviceTreeItem, value: number) {
        let nomeclatureItem = this.findItem(this.treeDataAll, dataItem.uid);
        if (nomeclatureItem) {
            nomeclatureItem.checked = value !== 0;
            nomeclatureItem.neededCount2 = value;
            nomeclatureItem.neededCount = 0;

            if (!nomeclatureItem.checked) {
                nomeclatureItem.cost = nomeclatureItem.originalCost;
                dataItem.cost = nomeclatureItem.originalCost;
            }

            this.addOrUpdateSelectedDevicesCount(dataItem.uid, 0, value);
        }
    }

    isPriceEditable(dataItem: DeviceTreeItem): boolean {
        return !!dataItem.checked || (dataItem.neededCount != null && dataItem.neededCount > 0)
            || (dataItem.neededCount2 != null && dataItem.neededCount2 > 0);
    }

    changeCost(dataItem: DeviceTreeItem, value: number) {
        let cost = value;
        if (cost == null || isNaN(Number(cost)) || Number(cost) < 0) {
            cost = 0;
        }

        dataItem.cost = Number(cost);

        let nomeclatureItem = this.findItem(this.treeDataAll, dataItem.uid);
        if (nomeclatureItem) {
            nomeclatureItem.cost = dataItem.cost;
        }
    }

    onCostKeyDown(event: KeyboardEvent) {
        if (event.key === '-' || event.key === 'e' || event.key === 'E' || event.key === '+') {
            event.preventDefault();
        }
    }

    addOrUpdateSelectedDevicesCount(key: string, count1: number, count2: number) {
        let device = this.selectedDevices.find(f => f.key == key);

        if (device) {
            if (count1 !== null) {
                device.count1 = count1;
            }

            if (count2 !== null) {
                device.count2 = count2;
            }
        } else {
            if (count1 === null) {
                count1 = 0;
            }

            if (count2 === null) {
                count2 = 0;
            }

            this.selectedDevices.push(new KeyAndCount(key, count1, count2));
        }
    }

    get selectedDevicesCount(): number {
        let counts = this.selectedDevices.map(m => m.count1 + m.count2);

        const sum = (counts || []).reduce((acc, value) => acc + value, 0);
        return sum;
    }

    get totalPrice() {
        let filteredData = [];
        this.treeDataAll.forEach(requestCategoryItem => {
            requestCategoryItem.items.forEach(deviceTypeItem => {
                deviceTypeItem.items.forEach(deviceSupplierItem => {
                    deviceSupplierItem.items.forEach(nomenclatureItem => {
                        if (nomenclatureItem.checked) {
                            filteredData.push({
                                cost: nomenclatureItem.cost,
                                quantity: nomenclatureItem.neededCount,
                                quantity2: nomenclatureItem.neededCount2
                            });
                        }
                    });
                });
            });
        });

        const totalPrice = filteredData.reduce((sum, item) => sum + (item.cost * item.quantity) + (item.cost * item.quantity2), 0);
        return totalPrice;
    }

    chooseNomenclature(dataItem: DeviceTreeItem) {
        if (dataItem.disabled) {
            return;
        }

        dataItem.checked = !dataItem.checked;
        this.checkItem(dataItem, dataItem.checked);
    }

    expandOrCollapseGroup(dataItem: DeviceTreeItem) {
        if (this.expandedKeys.some(x => x == dataItem.uid)) {
            this.expandedKeys = this.expandedKeys.filter(x => x != dataItem.uid);
        }
        else {
            this.expandedKeys.push(dataItem.uid);
        }
    }

    saveChanges() {
        if (this.isApplying) {
            return;
        }
        this.isApplying = true;
        this.loading = true;

        let requestDevices: NewRequestDevice[] = [];

        this.treeDataAll.forEach(requestCategoryItem => {
            requestCategoryItem.items.forEach(deviceTypeItem => {
                deviceTypeItem.items.forEach(deviceSupplierItem => {
                    deviceSupplierItem.items.forEach(nomenclatureItem => {
                        if (!nomenclatureItem.checked) {
                            return;
                        }

                        const quantity1 = Number(nomenclatureItem.neededCount);
                        if (isFinite(quantity1) && quantity1 > 0) {
                            requestDevices.push({
                                deviceTypeId: deviceTypeItem.id,
                                deviceSupplierId: deviceSupplierItem.id,
                                deviceModelId: nomenclatureItem.deviceModelId,
                                nomenclature: nomenclatureItem.name,
                                cost: nomenclatureItem.cost,
                                fullCost: nomenclatureItem.cost,
                                warehouseIntegraCode: nomenclatureItem.warehouseIntegraCode,
                                deviceModelName: nomenclatureItem.deviceModelName,
                                quantity: quantity1,
                                articleName: nomenclatureItem.sku
                            } as NewRequestDevice);
                        }

                        if (!this.hideWarehouses) {
                            const quantity2 = Number(nomenclatureItem.neededCount2);
                            if (isFinite(quantity2) && quantity2 > 0) {
                                requestDevices.push({
                                    deviceTypeId: deviceTypeItem.id,
                                    deviceSupplierId: deviceSupplierItem.id,
                                    deviceModelId: nomenclatureItem.deviceModelId,
                                    nomenclature: nomenclatureItem.name,
                                    cost: nomenclatureItem.cost,
                                    fullCost: nomenclatureItem.cost,
                                    warehouseIntegraCode: nomenclatureItem.warehouseIntegraCode2,
                                    deviceModelName: nomenclatureItem.deviceModelName,
                                    quantity: quantity2,
                                    articleName: nomenclatureItem.sku
                                } as NewRequestDevice);
                            }
                        }
                    });
                });
            });
        });

        this.onContinue.emit(requestDevices);
    }

    cancel() {
        this.onCancel.emit();
    }

    public hasChildren(item: DeviceTreeItem): boolean {
        return (!item.isNomenclature && item.items != null && item.items.length != 0) || item.isDeviceSupplier;
    }

    public fetchChildren(item: DeviceTreeItem): Observable<DeviceTreeItem[]> {
        if (this.hideWarehouses) {
            return of(item.items || []);
        }
        if (item.isDeviceSupplier && (item.items == null || item.items.length == 0)) {
            return this.nomenclatureService.getNomenclatureByDeviceModels(item.deviceModels.map(m => m.id), this.requestId, this.isReclamation, this.serviceCenterId)
                .pipe(
                    map((response: ActionDataResult<DeviceModelNomenclatureDto[]>) => {
                        if (response.isSuccessful && response.data.length > 0) {
                            item.emptyNomeclature = false;
                            item.items = response.data.map(nomenclature => {
                                let itemDisabled = this.disableFreeItems && (nomenclature.cost == null || nomenclature.cost == 0);
                                itemDisabled = nomenclature.count === 0 ? false : itemDisabled;
                                return {
                                    name: nomenclature.nomenclature,
                                    sku: nomenclature.sku,
                                    isNomenclature: true,
                                    cost: nomenclature.cost,
                                    originalCost: nomenclature.cost,
                                    uid: Guid.create().toString(),
                                    count: nomenclature.count,
                                    count2: nomenclature.count2,
                                    countAlias: nomenclature.countAlias,
                                    warehouseIntegraCode: nomenclature.warehouseIntegraCode,
                                    warehouseIntegraCode2: nomenclature.warehouseIntegraCode2,
                                    warehouseName: nomenclature.warehouseName,
                                    warehouseName2: nomenclature.warehouseName2,
                                    costAlias: nomenclature.costAlias,
                                    disabled: itemDisabled,
                                    neededCount: 0,
                                    neededCount2: 0,
                                    deviceModelId: nomenclature.deviceModelId,
                                    deviceModelName: nomenclature.deviceModelName,
                                    articleNumber: nomenclature.articleNumber
                                } as DeviceTreeItem;
                            });
                        }
                        else {
                            item.items = this.buildEmptyNomenclature();
                        }

                        this.setChildren(item.uid, item.items);
                        return item.items;
                    })
                );
        }
        else {
            return of(item.items);
        }
    }

    setChildren(uid: string, items: DeviceTreeItem[] = []) {
        let item = this.findItem(this.treeDataAll, uid);
        item.items = items;
    }

    findItem(items: DeviceTreeItem[], uid: string): DeviceTreeItem {
        for (let item of items) {
            if (item.uid === uid) {
                return item;
            }
            if (item.items && item.items.length > 0) {
                const foundItem = this.findItem(item.items, uid);
                if (foundItem) {
                    return foundItem;
                }
            }
        }
        return null;
    }

    hasChecked(dataItem: DeviceTreeItem) {
        return dataItem.items && dataItem.items.some(x => x.checked || (dataItem.items && dataItem.items.some(x => this.hasChecked(x))));
    }

    private buildEmptyNomenclature(): DeviceTreeItem[] {
        return [{
            name: "Номенклатура не найдена",
            isNomenclature: true,
            emptyNomeclature: true,
            uid: Guid.create().toString()
        } as DeviceTreeItem];
    }

    private nameIsMatch(item: DeviceTreeItem, value: string): boolean {
        return item.name.toLowerCase().includes(value.toLowerCase());
    }

    private skuIsMatch(item: DeviceTreeItem, value: string): boolean {
        return !!item.sku && item.sku.toLowerCase().includes(value.toLowerCase());
    }

    private distinct(values: number[] = []): number[] {
        return Array.from(new Set(values));
    }

    @HostListener('wheel', ['$event'])
    onWheel(e: WheelEvent) {
        // если внутри есть скроллируемый контейнер и он может скроллиться — оставляем
        // иначе блокируем, чтобы не скроллился body
        e.preventDefault();
        e.stopPropagation();
    }
}